/**
 * Alta de orden de trabajo.
 *
 * El campo que no se puede tratar como uno más es la **fecha límite
 * legal**: no es una fecha de compromiso comercial, es el tope del art.
 * 21.1 para el grupo de obligaciones que la orden cubre. Pasarla no es
 * llegar tarde, es incumplir, y por eso la pantalla lo dice donde se
 * rellena en lugar de dejarlo para una alerta posterior.
 *
 * ## De dónde salen las fechas
 *
 * Al elegir el centro se consulta el plan de mantenimiento y se propone la
 * visita: qué obligaciones vencen, cuándo, y cuánto se tarda. Hasta ahora
 * esa propuesta existía en el servidor pero ninguna pantalla la usaba, así
 * que las órdenes creadas desde aquí **no cubrían ninguna obligación**: el
 * plan seguía diciendo que esos extintores estaban pendientes después de
 * haberlos revisado.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { useForm } from '../../forms/use-form.js';
import { FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { isoDate, maxLength, type FieldRules } from '../../forms/form-state.js';

const TYPES = [
  { value: 'PREVENTIVO', label: 'Preventivo' },
  { value: 'CORRECTIVO', label: 'Correctivo' },
  { value: 'AVISO', label: 'Aviso' },
  { value: 'INSTALACION', label: 'Instalación' },
  { value: 'TALLER', label: 'Taller' },
  { value: 'OTROS', label: 'Otros' },
];

const PRIORITIES = [
  { value: 'URGENTE', label: 'Urgente' },
  { value: 'ALTA', label: 'Alta' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'BAJA', label: 'Baja' },
];

/** Lo que devuelve `/planning/proposed-visits` para un centro. */
interface Obligation {
  planItemId: string;
  equipmentId: string;
  ruleCode: string;
  operationName: string;
  systemCode: string;
  dueDate: string | null;
}

interface ProposedVisit {
  siteId: string;
  siteName: string;
  proposedDate: string;
  legalDeadline: string;
  obligations: Obligation[];
  equipmentCount: number;
  estimatedMinutes: number;
  estimatedHours: string;
  urgent: boolean;
  overdue: boolean;
}

const RULES: FieldRules = {
  scheduledDate: [isoDate()],
  legalDeadline: [isoDate()],
  contactName: [maxLength(255)],
  contactPhone: [maxLength(32)],
};

export function WorkOrderFormScreen(): React.ReactNode {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { api } = useSession();
  const [siteId, setSiteId] = useState(params.get('siteId') ?? '');
  const [siteError, setSiteError] = useState<string | undefined>(undefined);

  const [visit, setVisit] = useState<ProposedVisit | null>(null);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [loadingVisit, setLoadingVisit] = useState(false);

  const form = useForm<{ id: string; number: string }>({
    mode: 'create',
    rules: RULES,
    nullable: ['scheduledDate', 'legalDeadline', 'estimatedMinutes', 'contractId'],
    initial: {
      workOrderType: 'PREVENTIVO',
      priority: 'MEDIA',
      scheduledDate: '',
      legalDeadline: '',
      estimatedMinutes: '',
      contactName: '',
      contactPhone: '',
      instructions: '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = { ...body, siteId };
      if (typeof payload['estimatedMinutes'] === 'string' && payload['estimatedMinutes'] !== '') {
        payload['estimatedMinutes'] = Number(payload['estimatedMinutes']);
      }
      // Sin esto la orden nace vacía y el plan sigue diciendo que esos
      // equipos están pendientes después de haberlos revisado.
      if (chosen.size > 0) payload['planItemIds'] = [...chosen];
      return client.post<{ id: string; number: string }>('/work-orders', payload);
    },
    onSaved: () => void navigate('/ordenes'),
  });

  /**
   * Trae la propuesta del plan para el centro elegido.
   *
   * Si el usuario no tiene permiso de planificación, la petición falla y el
   * formulario sigue funcionando sin propuesta: poder crear una orden a mano
   * no puede depender de un permiso que sólo sirve para sugerir.
   */
  const loadVisit = useCallback(
    async (site: string) => {
      if (site === '') {
        setVisit(null);
        setChosen(new Set());
        return;
      }
      setLoadingVisit(true);
      try {
        const data = await api.get<{ visits: ProposedVisit[] }>(
          `/planning/proposed-visits?siteId=${encodeURIComponent(site)}`,
        );
        const propuesta = data.visits.find((v) => v.siteId === site) ?? null;
        setVisit(propuesta);
        // Todas marcadas de salida: el caso normal es cubrir lo que vence,
        // y quitar dos casillas cuesta menos que marcar dieciocho.
        setChosen(new Set(propuesta?.obligations.map((o) => o.planItemId) ?? []));
      } catch {
        setVisit(null);
        setChosen(new Set());
      } finally {
        setLoadingVisit(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadVisit(siteId);
  }, [siteId, loadVisit]);

  const scheduled = String(form.values['scheduledDate'] ?? '');
  const deadline = String(form.values['legalDeadline'] ?? '');
  /** Programar después del tope legal es planificar un incumplimiento. */
  const scheduledAfterDeadline = scheduled !== '' && deadline !== '' && scheduled > deadline;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (siteId === '') {
      setSiteError('Elija el centro donde se hace el trabajo.');
      return;
    }
    setSiteError(undefined);
    await form.submit();
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nueva orden de trabajo</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {form.formError !== null && (
        <p className="screen__error" role="alert">
          {form.formError}
        </p>
      )}

      {scheduledAfterDeadline && (
        <p className="screen__error" role="alert">
          La fecha programada es <strong>posterior a la fecha límite legal</strong>. Se puede guardar
          —a veces no hay otra— pero es un incumplimiento del art. 21.1 planificado a sabiendas, y
          quedará registrado como tal.
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)} noValidate>
        <FormSection title="Trabajo">
          <EntityPicker
            label="Centro"
            entity="SITE"
            value={siteId}
            onChange={(v) => {
              setSiteId(v);
              setSiteError(undefined);
            }}
            error={siteError}
            required
          />
          {loadingVisit && <p className="screen__note">Consultando el plan de mantenimiento…</p>}

          {visit !== null && (
            <div className="propuesta">
              <p>
                El plan de este centro tiene <strong>{visit.obligations.length} obligaciones</strong>{' '}
                sobre {visit.equipmentCount} equipos ({visit.estimatedHours}).{' '}
                {visit.overdue ? (
                  <strong className="propuesta__alerta">Ya hay vencimientos pasados.</strong>
                ) : visit.urgent ? (
                  <strong className="propuesta__alerta">
                    Queda poco margen: conviene programarla ya.
                  </strong>
                ) : null}
              </p>
              <p>
                Fecha propuesta <strong>{visit.proposedDate}</strong> · tope legal{' '}
                <strong>{visit.legalDeadline}</strong>
              </p>
              {/*
                No se escriben las fechas encima de lo que alguien haya
                tecleado. Rellenar un hueco vacío ayuda; pisar un dato
                escrito a mano es perder trabajo sin avisar.
              */}
              <button
                className="button button--quiet button--small"
                type="button"
                onClick={() => {
                  form.set('scheduledDate', visit.proposedDate);
                  form.set('legalDeadline', visit.legalDeadline);
                  form.set('estimatedMinutes', String(visit.estimatedMinutes));
                }}
              >
                Usar las fechas propuestas
              </button>

              <ul className="propuesta__obligaciones">
                {visit.obligations.map((o) => (
                  <li key={o.planItemId}>
                    <label>
                      <input
                        type="checkbox"
                        checked={chosen.has(o.planItemId)}
                        onChange={(e) => {
                          const siguiente = new Set(chosen);
                          if (e.target.checked) siguiente.add(o.planItemId);
                          else siguiente.delete(o.planItemId);
                          setChosen(siguiente);
                        }}
                      />{' '}
                      {o.operationName} · {o.systemCode}
                      {o.dueDate !== null ? ` · vence ${o.dueDate}` : ' · sin fecha de vencimiento'}
                    </label>
                  </li>
                ))}
              </ul>
              <p className="screen__note">
                Lo que deje marcado queda cubierto por esta orden y sale de la lista de pendientes
                del plan. Lo que desmarque sigue pendiente.
              </p>
            </div>
          )}

          {visit === null && siteId !== '' && !loadingVisit && (
            <p className="screen__note">
              Este centro no tiene obligaciones pendientes en el plan. La orden se creará sin cubrir
              ninguna: para un aviso o un correctivo es lo normal.
            </p>
          )}

          <SelectField
            label="Tipo"
            value={String(form.values['workOrderType'] ?? '')}
            onChange={(v) => form.set('workOrderType', v)}
            options={TYPES}
            required
          />
          <SelectField
            label="Prioridad"
            value={String(form.values['priority'] ?? '')}
            onChange={(v) => form.set('priority', v)}
            options={PRIORITIES}
            required
            help="La prioridad urgente arranca el reloj del SLA del contrato."
          />
          <TextField
            label="Duración estimada (min)"
            type="number"
            value={String(form.values['estimatedMinutes'] ?? '')}
            onChange={(v) => form.set('estimatedMinutes', v)}
            help="Alimenta el cálculo de carga de trabajo del planificador."
          />
        </FormSection>

        <FormSection
          title="Fechas"
          description={
            <>
              La <strong>fecha límite legal</strong> no es un compromiso comercial: es el tope del
              art. 21.1 para las obligaciones que esta orden cubre. Pasarla es incumplir.
            </>
          }
        >
          <TextField
            label="Fecha programada"
            type="date"
            value={scheduled}
            onChange={(v) => form.set('scheduledDate', v)}
            onBlur={() => form.blur('scheduledDate')}
            error={form.errors['scheduledDate']}
          />
          <TextField
            label="Fecha límite legal"
            type="date"
            value={deadline}
            onChange={(v) => form.set('legalDeadline', v)}
            onBlur={() => form.blur('legalDeadline')}
            error={form.errors['legalDeadline']}
            help="Normalmente sale del plan de mantenimiento. Rellénela a mano sólo si sabe por qué."
          />
        </FormSection>

        <FormSection title="Contacto en el centro">
          <TextField
            label="Persona de contacto"
            value={String(form.values['contactName'] ?? '')}
            onChange={(v) => form.set('contactName', v)}
            onBlur={() => form.blur('contactName')}
            error={form.errors['contactName']}
          />
          <TextField
            label="Teléfono"
            type="tel"
            value={String(form.values['contactPhone'] ?? '')}
            onChange={(v) => form.set('contactPhone', v)}
            onBlur={() => form.blur('contactPhone')}
            error={form.errors['contactPhone']}
          />
          <TextArea
            label="Instrucciones para el técnico"
            value={String(form.values['instructions'] ?? '')}
            onChange={(v) => form.set('instructions', v)}
            rows={4}
            help="Lo que hay que saber antes de llegar: accesos, horarios, con quién hablar."
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear orden'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/ordenes');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
